import { Modal, Space, Tooltip, Spin, Button } from 'antd';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getOverTimeData, postSingleBuyApi, fetchInsMonitorSearch } from '@/lib/service';
import style from './index.module.less';
import classnames from 'classnames';
import { useTranslation } from 'next-i18next';
import type { SingleInfo, BuyoutListModel } from '@/types/home';
import Image from 'next/image';
import { getCurrencyInfo } from '@/components/Plan/LangCurrencyEnum';

const ActiveAccountModal: React.FC<any> = ({ isModalOpen, closeFn, routerInfo }) => {
  const { t } = useTranslation('common');
  const [allDataList, setAllDataList] = useState<SingleInfo[]>([]);
  const [newMounse, setNowMounse] = useState<BuyoutListModel[]>();
  const [selectAllReport, setSelectAllReport] = useState<BuyoutListModel[]>([]); //右侧内容选中
  const reportList = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false); // 添加加载状态
  const [buyLoading, setBuyLoading] = useState(false);
  const [myAvatar, setmyAvatar] = useState('');

  // 重置状态
  const resetState = useCallback(() => {
    setAllDataList([]);
    setNowMounse([]);
    setSelectAllReport([]);
    setmyAvatar('');
  }, []);

  // 选中
  const hanleSelectRow = (item: any) => {
    if (item.status == 1) {
      return;
    }
    let temp = selectAllReport.filter((row) => row.begin_time == item.begin_time);
    if (temp.length == 0) {
      setSelectAllReport([item, ...selectAllReport]);
    } else {
      let arr = selectAllReport.filter((row) => row.begin_time != item.begin_time);
      setSelectAllReport(arr);
    }
  };
  // 是否选中判断
  const isSelect = (item: any) => {
    let temp = selectAllReport.filter((row) => row.begin_time === item.begin_time);
    return temp.length > 0;
  };

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getOverTimeData(routerInfo.identification || routerInfo.id);
      if (res.code == 0) {
        const allDataList = res.data
          .reduce((acc, cur) => [...acc, ...cur.buyout_list], [])
          .sort((a, b) => +new Date(b.end_time) - +new Date(a.end_time));

        setAllDataList(res.data);
        setNowMounse(allDataList);

        if (res.data?.length > 0) {
          var tempTotal = 0;
          var tempList: any[] = [];
          for (let i = res.data.length - 1; i >= 0; i--) {
            const element = res.data[i]?.buyout_list;

            for (let j = element?.length - 1; j >= 0; j--) {
              const subElement = element[j];
              if (subElement?.status !== 1 && tempTotal < 6) {
                tempList.push(subElement);
                tempTotal += 1;
                if (tempTotal >= 6) {
                  break;
                }
              }
            }
          }
          setSelectAllReport(tempList);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [routerInfo]);

  const reportTotal = useMemo(() => {
    return allDataList.reduce((acc, item) => {
      const falseCount = item.buyout_list.filter((b) => !b.status).length;
      return acc + falseCount;
    }, 0);
  }, [allDataList]);

  useEffect(() => {
    if (isModalOpen) {
      getData();
      document.documentElement.style.overflow = 'hidden';
    } else {
      // 关闭模态框时重置状态
      resetState();
    }
    return () => {
      document.documentElement.style.overflow = 'auto';
    };
  }, [isModalOpen, getData, resetState]);

  useEffect(() => {
    if (isModalOpen && !routerInfo?.avatar) {
      fetchInsMonitorSearch(routerInfo.name, undefined).then((res: any) => {
        setmyAvatar(res.data[0].profile_pic_url);
      });
    }
  }, [isModalOpen, routerInfo]);

  useEffect(() => {
    const currentIndex = newMounse?.findIndex((item) => item?.status === 0);
    const itemHeight = Math.floor(reportList?.current?.scrollHeight / newMounse?.length);
    reportList.current?.scrollTo({
      top: itemHeight * (currentIndex < 0 ? 0 : currentIndex),
    });
  }, [newMounse]);

  // 购买
  const handlePostBal = async () => {
    if (selectAllReport.length === 0) {
      return;
    }

    try {
      setBuyLoading(true);
      const arr = selectAllReport.map((item) => ({
        begin_date: item.begin_time,
        end_date: item.end_time,
        record_id: item.record_id,
      }));

      const params = {
        identification: routerInfo.identification || routerInfo.id,
        params: arr,
      };

      const res = await postSingleBuyApi(params);

      if (res && res.data?.client_secret) {
        window.location.href = res.data.pay_link;
      }
    } catch (error) {
      console.error('Payment failed:', error);
    } finally {
      setBuyLoading(false);
    }
  };

  // 检查是否已选择前18个报告
  const isSelected18 = useMemo(() => {
    if (!newMounse || !selectAllReport.length) return false;

    const selectedItems = newMounse.filter((item) => item.status !== 1).slice(0, 18);
    if (selectedItems.length !== selectAllReport.length) return false;

    return selectedItems.every((item) => selectAllReport.some((selected) => selected.begin_time === item.begin_time));
  }, [newMounse, selectAllReport]);

  // 检查是否已选择全部可用报告
  const isSelectedAll = useMemo(() => {
    if (!newMounse || !selectAllReport.length) return false;

    const allItems = newMounse.filter((item) => item.status !== 1);
    if (allItems.length !== selectAllReport.length) return false;

    return allItems.every((item) => selectAllReport.some((selected) => selected.begin_time === item.begin_time));
  }, [newMounse, selectAllReport]);

  // 选择18条
  const handleSelect18 = () => {
    if (isSelected18) {
      setSelectAllReport([]);
    } else {
      setSelectAllReport(newMounse.filter((item) => item.status !== 1).slice(0, 18));
    }
  };

  // 选择全部
  const handleAllReport = () => {
    if (isSelectedAll) {
      setSelectAllReport([]);
    } else {
      setSelectAllReport(newMounse.filter((item) => item.status !== 1));
    }
  };

  const [browserLanguage, setBrowserLanguage] = useState<string | null>(null);

  // 检测浏览器语言
  useEffect(() => {
    // 检查 navigator 对象是否存在于客户端
    if (typeof window !== 'undefined' && navigator.language) {
      // 获取浏览器的首选语言
      const browserLanguage = navigator.language;
      const prefix = browserLanguage.split('-')[0];
      // 设置语言前缀
      setBrowserLanguage(prefix);
    }
  }, []);
  const currencyInfo = getCurrencyInfo(browserLanguage);

  return (
    <>
      <Modal
        className={style.model}
        destroyOnHidden
        open={isModalOpen}
        onCancel={() => closeFn()}
        footer={null}
        width="450px"
      >
        <div className={style.modalContent} style={{ padding: '20px 24px' }}>
          <div className={style.right}>
            <div className={style.header}>{t('RequestRetrospectiveDatafor')}</div>
            <div className={style.userInfo}>
              <img src={routerInfo?.avatar || myAvatar} alt="" />
              <span>{routerInfo?.name || routerInfo?.media_name}</span>
              <Image
                className={style.icon}
                alt="checkicon"
                src={'/images/report/check.png'}
                width={14}
                height={14}
              ></Image>
            </div>
            <div className={style.desc}>
              <div>
                {t('You can access')}
                <span>
                  {' ' + t('up to')}
                  {reportTotal + ' '}
                  {t('past reports')}.
                </span>
                {t('Naturally, longer tracking periods provide better insights')}
              </div>
              <div>{t('P.S. Please note that Past reports do not include Stories & Highlights.')}</div>
            </div>
            <div className={style.starTip}>
              <Tooltip
                placement="bottomRight"
                color={'#fff'}
                title={<p style={{ color: '#000', padding: '4px' }}>{t('Star tip')}</p>}
              >
                <span className={style.tipText}>{t('Learn about report delivery times')}</span>
              </Tooltip>
            </div>

            <Space className={style.selectButton}>
              <Button
                className={classnames(
                  style.selectBtn,
                  newMounse?.every((item) => item.status === 1) ? style.disablAll : '',
                  isSelectedAll ? style.activeBtn : ''
                )}
                onClick={handleAllReport}
              >
                {t('Select All')}
              </Button>

              {reportTotal >= 18 && (
                <Button
                  className={classnames(style.selectBtn, isSelected18 ? style.activeBtn : '')}
                  onClick={handleSelect18}
                >
                  {t('Select 18 Reports')}
                </Button>
              )}
            </Space>

            <div className={style.reportListContent}>
              {loading ? (
                <div className="h-full flex justify-center items-center">
                  <Spin spinning={loading} tip={t('Loading')}></Spin>
                </div>
              ) : (
                <div className={style.reports} ref={reportList}>
                  {newMounse?.map((item, idx: number) => (
                    <div
                      key={idx}
                      onClick={(e) => hanleSelectRow(item)}
                      className={classnames(style.leftListItem, isSelect(item) ? style.leftListItemAc : '')}
                    >
                      <div className={style.dateContent}>
                        <div
                          className={style.secondRow}
                          style={{ color: item.status == 1 ? 'rgba(217, 217, 217, 1)' : '' }}
                        >
                          <span>{item.begin_time}</span>
                          <span>-</span>
                          <span>{item.end_time}</span>
                        </div>
                      </div>

                      {item.status == 1 && <div className={style.purchased}>{t('Purchased')}</div>}
                      {item.has_data ? <img src="/images/starInfo/yellowStar.webp" alt="" /> : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={style.selectHead}>
              <div className={style.reportTotal}>
                <span>{selectAllReport.length}</span>/ {reportTotal + ' '}
                {t('Selected')} {t('reports')}
              </div>
              <div className={style.accountMoney}>
                {currencyInfo.symbol}
                {currencyInfo.price} / {t('reports')}
              </div>
            </div>

            <Button
              className={classnames(style.btnPay, selectAllReport.length === 0 ? style.btnDisabled : '')}
              onClick={handlePostBal}
              loading={buyLoading}
            >
              <Space>
                <span className={style.moneyTotal}>
                  {currencyInfo.symbol} {(currencyInfo.price * selectAllReport.length).toFixed(2)}
                </span>
                <span className={style.getNow}>{t('Get Now!')}</span>
              </Space>
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const payWrapper = (data: any) => {
  return <ActiveAccountModal {...data}></ActiveAccountModal>;
};
export default payWrapper;
